import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { StreamModality, VideoFrame } from '@ethbridge/core';
import { useBridgeContext, useBridgeSelector } from '../context/BridgeContext';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Video stream options
 */
export interface UseVideoStreamOptions {
  /** Default modality */
  modality?: StreamModality;

  /** Auto-start streaming on mount */
  autoStart?: boolean;

  /** Callback on each frame */
  onFrame?: (frame: VideoFrame) => void;

  /** Callback when streaming starts */
  onStart?: () => void;

  /** Callback when streaming stops */
  onStop?: () => void;
}

/**
 * Return type for useVideoStream hook
 */
export interface UseVideoStreamReturn {
  /** Video source URL (data URL) */
  src: string | null;

  /** Whether streaming is active */
  isStreaming: boolean;

  /** Number of frames received */
  frameCount: number;

  /** Last received frame */
  lastFrame: VideoFrame | null;

  /** Frames per second (approximate) */
  fps: number;

  /** Start streaming */
  start: (modality?: StreamModality) => void;

  /** Stop streaming */
  stop: () => void;

  /** Toggle streaming */
  toggle: (modality?: StreamModality) => void;

  /** Ref for video element */
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for video streaming
 *
 * @param options - Stream options
 * @returns Video stream state and controls
 *
 * @example
 * ```tsx
 * function VideoPreview() {
 *   const {
 *     src,
 *     isStreaming,
 *     frameCount,
 *     fps,
 *     start,
 *     stop,
 *     toggle,
 *   } = useVideoStream({
 *     modality: 'face',
 *     onFrame: (frame) => console.log('Frame:', frame.width, 'x', frame.height),
 *   });
 *
 *   return (
 *     <div>
 *       {src && <img src={src} alt="Video preview" />}
 *       <p>Frames: {frameCount} ({fps.toFixed(1)} FPS)</p>
 *       <button onClick={() => toggle()}>
 *         {isStreaming ? 'Stop' : 'Start'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useVideoStream(options: UseVideoStreamOptions = {}): UseVideoStreamReturn {
  const { modality = 'face', autoStart = false, onFrame, onStart, onStop } = options;

  const context = useBridgeContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const fpsRef = useRef({ count: 0, lastTime: Date.now(), fps: 0 });

  // Video state
  const src = useBridgeSelector((state) => state.video.src);
  const isStreaming = useBridgeSelector((state) => state.video.isStreaming);
  const frameCount = useBridgeSelector((state) => state.video.frameCount);
  const lastFrame = useBridgeSelector((state) => state.video.lastFrame);

  // Calculate FPS
  const fps = useMemo(() => {
    const now = Date.now();
    const elapsed = now - fpsRef.current.lastTime;

    if (elapsed >= 1000) {
      fpsRef.current.fps = (fpsRef.current.count / elapsed) * 1000;
      fpsRef.current.count = 0;
      fpsRef.current.lastTime = now;
    }

    fpsRef.current.count++;
    return fpsRef.current.fps;
  }, [frameCount]);

  // Start streaming
  const start = useCallback(
    (streamModality?: StreamModality) => {
      context.startStream(streamModality ?? modality);
      onStart?.();
    },
    [context, modality, onStart]
  );

  // Stop streaming
  const stop = useCallback(() => {
    context.stopStream();
    onStop?.();
  }, [context, onStop]);

  // Toggle streaming
  const toggle = useCallback(
    (streamModality?: StreamModality) => {
      if (isStreaming) {
        stop();
      } else {
        start(streamModality);
      }
    },
    [isStreaming, start, stop]
  );

  // Auto-start on mount
  useEffect(() => {
    if (autoStart) {
      start();
    }

    return () => {
      if (isStreaming) {
        stop();
      }
    };
  }, [autoStart]); // Only run on mount/unmount

  // Call onFrame callback
  useEffect(() => {
    if (lastFrame && onFrame) {
      onFrame(lastFrame);
    }
  }, [lastFrame, onFrame]);

  return useMemo(
    () => ({
      src,
      isStreaming,
      frameCount,
      lastFrame,
      fps,
      start,
      stop,
      toggle,
      videoRef,
    }),
    [src, isStreaming, frameCount, lastFrame, fps, start, stop, toggle]
  );
}
